import axios from "axios";
import env from "../variables/config";

axios.defaults.baseURL = env.apiUrl;

/**
 * Support chat: the widget (components/support/SupportWidget) and the back-office (/bo/support).
 * The widget works logged in or not: without a session, a random key kept in localStorage
 * identifies the visitor's conversations.
 */

export type SupportStatus = "open" | "answered" | "closed";

export interface SupportMessage {
    _id: string;
    from: "user" | "admin";
    body: string;
    authorEmail?: string;
    agent?: string; // who an admin reply is shown as, see components/support/agents.ts
    createdAt: string;
}

export interface SupportTicketSummary {
    _id: string;
    subject: string;
    status: SupportStatus;
    createdAt: string;
    lastMessageAt: string;
    unread: boolean;
    lastMessage: SupportMessage | null;
}

export interface SupportTicket extends Omit<SupportTicketSummary, "lastMessage"> {
    messages: SupportMessage[];
}

const VISITOR_KEY = "support_visitor_key";

function visitorKey(): string {
    try {
        let key = localStorage.getItem(VISITOR_KEY);
        if (!key) {
            key = Array.from(crypto.getRandomValues(new Uint8Array(20)), (b) => b.toString(16).padStart(2, "0")).join("");
            localStorage.setItem(VISITOR_KEY, key);
        }
        return key;
    } catch {
        return "";
    }
}

const widgetConfig = () => {
    const token = localStorage.getItem("token");
    return { headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}), "X-Support-Visitor": visitorKey() } };
};
const adminConfig = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });

export const supportErrorMessage = (e: any) => e?.response?.data?.error || "Une erreur est survenue, réessayez.";

// ── Widget ──────────────────────────────────────────────────────────────

export const getMyTickets = async () => (await axios.get("/support/tickets", widgetConfig())).data.tickets as SupportTicketSummary[];

export const getMyUnreadCount = async () => (await axios.get("/support/unread", widgetConfig())).data.count as number;

export const getMyTicket = async (id: string) => (await axios.get(`/support/tickets/${id}`, widgetConfig())).data.ticket as SupportTicket;

export const createTicket = async (fields: { body: string; subject?: string; email?: string; name?: string; page?: string }) =>
    (await axios.post("/support/tickets", fields, widgetConfig())).data.ticket as SupportTicket;

export const sendTicketMessage = async (id: string, body: string) =>
    (await axios.post(`/support/tickets/${id}/messages`, { body }, widgetConfig())).data.ticket as SupportTicket;

// ── Back-office ─────────────────────────────────────────────────────────

export interface AdminSupportTicketRow {
    _id: string;
    user?: string;
    email: string;
    name?: string;
    subject: string;
    status: SupportStatus;
    page?: string;
    lastMessageAt: string;
    lastMessageFrom: "user" | "admin";
    unreadByAdmin: boolean;
    createdAt: string;
    lastMessage: SupportMessage | null;
    company?: string;
    isSub: boolean;
}

export interface AdminSupportTicket extends Omit<AdminSupportTicketRow, "lastMessage" | "company" | "isSub"> {
    messages: SupportMessage[];
    closedAt?: string;
    closedBy?: string;
}

export interface AdminSupportUser {
    _id: string;
    email: string;
    givenName?: string;
    familyName?: string;
    company?: string;
    phone?: string;
    isSub?: boolean;
    trialEndsAt?: string;
    createdAt?: string;
    lastConnection?: string;
}

export interface AdminSupportList {
    tickets: AdminSupportTicketRow[];
    total: number;
    page: number;
    totalPages: number;
    counts: { open: number; answered: number; closed: number; unread: number };
}

export const getSupportTickets = async (params: { status?: string; q?: string; page?: number }) =>
    (await axios.get("/admin/support", { ...adminConfig(), params })).data as AdminSupportList;

export const getSupportUnreadCount = async () => (await axios.get("/admin/support/unread-count", adminConfig())).data.count as number;

export const getSupportTicket = async (id: string) =>
    (await axios.get(`/admin/support/${id}`, adminConfig())).data as {
        ticket: AdminSupportTicket;
        user: AdminSupportUser | null;
        otherTickets: { _id: string; subject: string; status: SupportStatus; lastMessageAt: string }[];
    };

export const replySupportTicket = async (id: string, body: string, opts: { close?: boolean; notifyByEmail?: boolean; agent?: string } = {}) =>
    (await axios.post(`/admin/support/${id}/messages`, { body, ...opts }, adminConfig())).data.ticket as AdminSupportTicket;

export const updateSupportTicket = async (id: string, fields: { status?: SupportStatus; unreadByAdmin?: boolean }) =>
    (await axios.patch(`/admin/support/${id}`, fields, adminConfig())).data.ticket as AdminSupportTicket;

export const deleteSupportTicket = async (id: string) => {
    await axios.delete(`/admin/support/${id}`, adminConfig());
};

// ── Widget home & help center (« Aide » tab) ────────────────────────────

export interface SupportHome {
    firstName: string | null;
    team: { firstName: string; initials: string }[];
    unread: number;
}

export interface HelpCollectionSummary {
    _id: string;
    title: string;
    description?: string;
    articleCount: number;
}

export interface HelpArticleRef {
    _id: string;
    title: string;
    collectionId?: string;
    excerpt?: string;
}

export interface HelpArticleFull {
    _id: string;
    title: string;
    body: string;
    collectionId: string;
    updatedAt: string;
}

export const getSupportHome = async () => (await axios.get("/support/home", widgetConfig())).data as SupportHome;

export const getHelpCenter = async () =>
    (await axios.get("/support/help")).data as { collections: HelpCollectionSummary[]; popular: HelpArticleRef[] };

export const getHelpCollection = async (id: string) =>
    (await axios.get(`/support/help/collections/${id}`)).data as { collection: { _id: string; title: string; description?: string }; articles: HelpArticleRef[] };

export const getHelpArticle = async (id: string) => (await axios.get(`/support/help/articles/${id}`)).data.article as HelpArticleFull;

export const searchHelp = async (q: string) => (await axios.get("/support/help/search", { params: { q } })).data.articles as HelpArticleRef[];

// ── Back-office: help center editor (/bo/help-center) ───────────────────

export interface AdminHelpCollection {
    _id: string;
    title: string;
    description?: string;
    order: number;
    published: boolean;
    articleCount: number;
    publishedCount: number;
}

export interface AdminHelpArticle {
    _id: string;
    collectionId: string;
    title: string;
    body?: string;
    order: number;
    published: boolean;
    views: number;
    updatedBy?: string;
    updatedAt: string;
}

export const getAdminHelpCollections = async () => (await axios.get("/admin/help-center/collections", adminConfig())).data.collections as AdminHelpCollection[];

export const createHelpCollection = async (fields: Partial<Pick<AdminHelpCollection, "title" | "description" | "published">>) =>
    (await axios.post("/admin/help-center/collections", fields, adminConfig())).data.collection as AdminHelpCollection;

export const updateHelpCollection = async (id: string, fields: Partial<Pick<AdminHelpCollection, "title" | "description" | "published" | "order">>) =>
    (await axios.patch(`/admin/help-center/collections/${id}`, fields, adminConfig())).data.collection as AdminHelpCollection;

export const deleteHelpCollection = async (id: string) => {
    await axios.delete(`/admin/help-center/collections/${id}`, adminConfig());
};

export const getAdminHelpArticles = async (collectionId: string) =>
    (await axios.get(`/admin/help-center/collections/${collectionId}/articles`, adminConfig())).data.articles as AdminHelpArticle[];

export const getAdminHelpArticle = async (id: string) => (await axios.get(`/admin/help-center/articles/${id}`, adminConfig())).data.article as AdminHelpArticle;

export const createHelpArticle = async (fields: Partial<Pick<AdminHelpArticle, "collectionId" | "title" | "body" | "published">>) =>
    (await axios.post("/admin/help-center/articles", fields, adminConfig())).data.article as AdminHelpArticle;

export const updateHelpArticle = async (id: string, fields: Partial<Pick<AdminHelpArticle, "collectionId" | "title" | "body" | "published" | "order">>) =>
    (await axios.patch(`/admin/help-center/articles/${id}`, fields, adminConfig())).data.article as AdminHelpArticle;

export const deleteHelpArticle = async (id: string) => {
    await axios.delete(`/admin/help-center/articles/${id}`, adminConfig());
};
